import type { ReactNode } from 'react';
import { Panel } from '@src/components/Panel/Panel';
import { Bar } from '@src/components/Bar/Bar';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { InfoIcon } from '@src/components/icons/InfoIcon';
import { WarningIcon } from '@src/components/icons/WarningIcon';
import { Typography } from '@src/components/Typography/Typography';
import type { PanelProps } from '@src/components/Panel/Panel';
import { CardDecoration } from '@src/theme/Decoration';
import './Card.css';

export type CardTone = 'support' | 'accent' | 'info' | 'success' | 'warning' | 'neutral';

export type CardSurface = 'tint' | 'plain';

const TONE_ICONS: Record<CardTone, ReactNode> = {
  support: <InfoIcon />,
  accent: <GiftIcon />,
  info: <InfoIcon />,
  success: <CheckIcon />,
  warning: <WarningIcon />,
  neutral: <InfoIcon />,
};

export interface CardProps {
  /** Semantic hint used to choose the default icon; all cards share a blue palette. */
  tone?: CardTone;
  /** tint — blue informational surface. plain — shared Panel surface. */
  surface?: CardSurface;
  /** Optional step mark, usually a number. */
  step?: ReactNode;
  /**
   * Leading mark on the title row. `true` picks the default for `tone`.
   * Omit it for a title-only or body-only card.
   */
  icon?: ReactNode | true;
  title?: string;
  /**
   * The card's control. Buttons belong here rather than in `children`: the
   * slot follows the card's alignment — to the right of the copy on a wide
   * start-aligned card, centred under it on a centred one — which is what
   * keeps a card from ending up half-centred.
   */
  action?: ReactNode;
  /**
   * Centre the copy — and the step disc, when there is one. A quiet hint
   * under a field, or a how-it-works step.
   */
  align?: 'start' | 'center';
  /** The copy. Anything that can be pressed goes in `action`. */
  children?: ReactNode;
  /** Optional seasonal edge decorations. */
  edges?: PanelProps['edges'] | false;
  /** Disable elevation when embedding a plain card inside another panel. */
  elevated?: boolean;
  /**
   * Children are a free slot (a form, a picker), not body copy. Skips the
   * type-role wrap so fields keep their own type.
   */
  slot?: boolean;
  /** Parent id. Title, body and step hang off it. */
  testId?: string;
  className?: string;
}

export function Card({
  tone = 'support',
  surface = 'tint',
  step,
  icon,
  title,
  action,
  align = 'start',
  children,
  edges,
  elevated = true,
  slot = false,
  testId,
  className,
}: CardProps) {
  const classes = ['card', `card--${tone}`, `card--${surface}`, `card--${align}`];
  if (edges && surface !== 'plain') classes.push('card--edges');
  if (slot) {
    classes.push('card--slot');
  }
  if (className) {
    classes.push(className);
  }

  const decoration = edges ? <CardDecoration variant={edges.pieces} /> : null;

  const mark = icon === true ? TONE_ICONS[tone] : icon;
  const hasCopy = Boolean(title || children);
  const stepBesideTitle = surface === 'plain' && Boolean(title);
  const stepBesideBody = surface === 'plain' && Boolean(step) && !title;

  if (stepBesideBody) {
    classes.push('card--step-inline');
  }

  const stepMark =
    step != null && step !== '' ? (
      <Typography
        as="span"
        role="body/strong"
        size="md"
        color="inherit"
        className="card__step"
        {...testIdAttr(testIdFor(testId, 'step'))}
      >
        {step}
      </Typography>
    ) : null;

  const titleNode = title ? (
    <Typography
      as="h3"
      role="title/card"
      color="inherit"
      className="card__title"
      {...testIdAttr(testIdFor(testId, 'title'))}
    >
      {title}
    </Typography>
  ) : null;

  const heading =
    stepBesideTitle && stepMark && titleNode ? (
      <div className="card__heading">
        {stepMark}
        {titleNode}
      </div>
    ) : (
      titleNode
    );

  const main = hasCopy ? (
    <div className="card__main">
      {mark ? <span className="card__icon">{mark}</span> : null}
      {stepBesideBody ? stepMark : null}
      <div className="card__copy">
        {heading}
        {children && (
          <Typography
            as="div"
            role={title || stepBesideBody ? 'body/base' : 'body/sm'}
            color="inherit"
            className="card__body"
            {...testIdAttr(testIdFor(testId, 'body'))}
          >
            {children}
          </Typography>
        )}
      </div>
    </div>
  ) : null;

  const actionSlot = action ? (
    <div className="card__action" {...testIdAttr(testIdFor(testId, 'action'))}>
      {action}
    </div>
  ) : null;

  /* A centred card reads down the middle, so the control belongs under the
   * copy rather than off to one side of it. */
  const body =
    actionSlot && align === 'start' ? (
      <Bar align="center" gap={5} wrap={false} stack="narrow">
        {main}
        {actionSlot}
      </Bar>
    ) : (
      <>
        {main}
        {actionSlot}
      </>
    );

  const slotBody = slot ? <div className="card__slot">{children}</div> : body;

  if (surface === 'plain') {
    return (
      <Panel
        elevated={elevated}
        wide
        edges={edges === false ? undefined : edges ?? (slot ? { pieces: 3 } : undefined)}
        className={classes.join(' ')}
        testId={testId}
      >
        {!slot && !stepBesideTitle && !stepBesideBody && stepMark}
        {slotBody}
      </Panel>
    );
  }

  return (
    <article className={classes.join(' ')} {...testIdAttr(testId)}>
      {decoration}
      {!slot && !stepBesideTitle && !stepBesideBody && stepMark}
      {slotBody}
    </article>
  );
}
